import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '../UI/TextField/TextField';
import Grid from '@mui/material/Grid';
import Button from '../UI/Buttons/Button';
import AutoCompleteField from '../UI/AutoCompleteField/AutoCompleteField';
import isEmpty from '../../is-empty';
import ModalDialog from '../UI/Dialog/ModalDialog';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { FormControl, FormLabel } from '@mui/material';
import { encryptString } from '../../helpers/securityHelper';
import common from '../../jsons/common.json';
import ValueHelper from '../../helpers/valueHelper';

function UnlockDialog(props) {
    const auth = useSelector(state => state.auth);
    const dispatch = useDispatch();
    const [pin, setPin] = useState('');
    const [reasons, setReasons] = useState([]);
    const [reasonSel, setReasonSel] = useState();
    const [statusSel, setStatusSel] = useState();
    const [comment, setComment] = useState('');
    const [enableSave, setEnableSave] = useState(true);
    const { t } = useTranslation('translation');

    const loadData = useCallback(async () => {
        const cacheValues = await ValueHelper.getPermCachedValues([common.cacheValues.editReason], auth.user.tenant);
        setReasons(cacheValues.editReasons);
        if(props.status != null && props.statuses != null && props.statuses.length > 0) {
            if(props.objectType === auth.constants.objectIds.productionOrder) {
                let status = null;
                if(props.status === auth.constants.productionOrderStatuses.finished){
                    status = props.statuses.find(s => s.value === auth.constants.productionOrderStatuses.reviewed);
                } else {
                    if(auth.constants.productionOrderStatuses.unlocked != null){
                        status = props.statuses.find(s => s.value === auth.constants.productionOrderStatuses.unlocked);
                    }
                    if(status == null){
                        status = props.statuses.find(s => s.value === auth.constants.productionOrderStatuses.inprogress);
                    }
                }
                setStatusSel(status);
            }
        }
    }, [props.status, props.statuses, auth, props.objectType]);

    useEffect(() => {
        if (props.show) {
            loadData();
        }
    }, [props.show, loadData]);

    const validate = useCallback(() => {
        let err = {};
        if (pin == null || pin === '') err.pin = t('required');
        if (reasonSel == null) err.reason = t('required');
        if (comment == null || comment === '') err.comment = t('required');
        if (!isEmpty(err)) {
            return false;
        } else {
            return true;
        }
    }, [pin, reasonSel, comment, t]);

    const _unlockClick = useCallback(async () => {
        let valid = validate();
        if (valid) {
            try {
                let pinEnc = encryptString(pin);
                let pinResult = await axios.post('/api/userpin', { userid: auth.user.id, pin: pinEnc });
                if (pinResult.data.signatureDocument != null && !pinResult.data.expired) {
                    let data = {
                        objectType: props.objectType,
                        id: props.id,
                        reason: reasonSel.value,
                        comment: comment,
                        status: statusSel != null ? statusSel.value : null
                    };
                    let result = await axios.post('/api/signature/unlock', data);
                    dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
                    try {
                        await Promise.resolve(props.callback?.());
                    } finally {
                        props.toggle();
                    }
                }
            } catch (err) {
                dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data, title: t('error'), severity: 'error' } });
            } finally {
                setEnableSave(true);
            }
        } else {
            setEnableSave(true);
        }
    }, [pin, auth, props, reasonSel, comment, t, validate, statusSel, dispatch]);

    const unlockClick = useCallback(async () => {
        if (!enableSave) return;
        setEnableSave(false);
        _unlockClick();
    }, [_unlockClick, enableSave]);

    const changeAuto = (name) => (e, newValue) => {
        if (name === 'reason') {
            setReasonSel(newValue);
        } else if (name === 'status') {
            setStatusSel(newValue);
        }
    };

    const buttonStack = useMemo(() => {
        const buttons = [
            <Button key="close" variant="closeDialog" onClick={props.toggle}>{t('cancel')}</Button>,
            <Button key="save" variant="save" onClick={() => unlockClick()} disabled={!enableSave}>{t('confirm')}</Button>
        ];
        return buttons;
    }, [props.toggle, unlockClick, enableSave, t]);

    const errors = props.errors != null ? props.errors : {};

    return (
        <ModalDialog buttonStack={buttonStack} toggleDialog={props.toggle} title={props.recordName != null ? t('unlock') + ' ' + props.recordName : t('unlock')}
            dialogStatus={props.show != null ? props.show : false} fullWidth maxWidth="sm">
            <Grid container spacing={2}>
                {props.status != null && props.statuses != null && props.statuses.length > 0 && props.status === auth.constants.productionOrderStatuses.finished &&
                    <Grid size={10}>
                        <AutoCompleteField
                        value={statusSel}
                        options={props.statuses}
                        onChange={changeAuto('status')}
                        error={errors.status != null ? true : false}
                        helperText={errors.status}
                        label={t('status')}
                    />
                    </Grid>
                }
                <Grid size={10}>
                    <AutoCompleteField
                        value={reasonSel}
                        options={reasons}
                        onChange={changeAuto('reason')}
                        error={errors.reason != null ? true : false}
                        helperText={errors.reason}
                        label={t('reason')}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField value={comment} onChange={e => setComment(e.target.value)} name="comment" multiline={true} rows="3"
                        variant="outlined" size="medium" fullWidth label={t('comment')}
                        error={errors.comment != null ? true : false} helperText={errors.comment} />
                </Grid>
                <Grid size={8}>
                    <FormControl>
                        <FormLabel>{t('user')}</FormLabel>
                        {auth.user.firstName + ' ' + auth.user.lastName}
                    </FormControl>
                </Grid>
                <Grid size={4}>
                    <TextField value={pin} onChange={e => setPin(e.target.value)} name="pin"
                        size="medium" fullWidth disabled={false} label={t('pin')} type={'password'}
                        error={errors.pin != null ? true : false} helperText={errors.pin} />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 0.75 }}>
                <Grid>
                    <Typography variant="body2" color="textSecondary">
                        {t('unlockConsent')}
                    </Typography>
                </Grid>
            </Grid>
        </ModalDialog>
    );
}

export default UnlockDialog;
